import React from 'react';
import { Container, Row, Col, Tabs, Tab, Accordion, ListGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { map, camelCase } from 'lodash';
import pluralize from 'pluralize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faDownload, faAngleRight } from '@fortawesome/free-solid-svg-icons';

import { PersonalAccidentQuote } from '@models/PersonalAccidentQuote';
import { tBenefitGroupType } from '@models/PersonalAccidentPlanBenefitGroup';
import { getPersonalAccidentPlanBenefitsByBenefitGroupTypes } from '@utils/personal-accident-quote';

import { Title } from './title';

interface DocumentItemProps {
  name: string;
  fileUrl?: string;
}

const DocumentItem = ({ name, fileUrl }: DocumentItemProps) => {
  return (
    <a href={fileUrl} className="link-dark text-decoration-none" target="_blank" rel="noreferrer">
      <span className="d-inline-block">
        <FontAwesomeIcon icon={faDownload} size="xl" className="text-primary me-2" />
        {name}
      </span>
    </a>
  );
};

type Props = Pick<PersonalAccidentQuote, 'personalAccidentPlanBenefits' | 'uncoverDescriptions' | 'productDocument'>;

const BenefitSection = ({ personalAccidentPlanBenefits, uncoverDescriptions, productDocument }: Props) => {
  const { t } = useTranslation();

  return (
    <section className="py-5 bg-light">
      <Title title={pluralize(t('common:benefit'), 2)} />
      <Container>
        <Row>
          <Col lg={12}>
            <Accordion className="mb-5" flush>
              {map(
                getPersonalAccidentPlanBenefitsByBenefitGroupTypes(personalAccidentPlanBenefits),
                (mappedBenefits, groupType) => (
                  <Accordion.Item eventKey={groupType} key={groupType}>
                    <Accordion.Header>
                      <span className="fw-bold">{tBenefitGroupType(groupType)}</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <ListGroup variant="flush">
                        {map(mappedBenefits, ({ benefitType, description }) => (
                          <ListGroup.Item key={benefitType}>
                            <FontAwesomeIcon icon={faAngleRight} size="sm" className="text-primary me-2" />
                            <strong>{t(`personalAccidentPlanBenefit.${camelCase(benefitType)}`)}:</strong> {description}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Accordion.Body>
                  </Accordion.Item>
                ),
              )}
            </Accordion>
          </Col>
        </Row>

        <Row>
          <Col lg={8} className="mx-auto">
            <Tabs
              defaultActiveKey="exclusions"
              variant="pills"
              className="d-flex flex-column flex-lg-row justify-content-center mb-5"
            >
              <Tab eventKey="exclusions" title={pluralize(t('common:exclusion'), 2)}>
                <Row className="gy-3">
                  {map(uncoverDescriptions, (description) => (
                    <Col lg={6} key={description}>
                      <div className="d-flex flex-row align-items-center">
                        <FontAwesomeIcon icon={faXmark} size="xl" className="text-danger me-2" />
                        {description}
                      </div>
                    </Col>
                  ))}
                </Row>
              </Tab>
              <Tab eventKey="documents" title={pluralize(t('common:document'), 2)}>
                <Row>
                  <Col>
                    <DocumentItem
                      name={t('healthQuoteDetailsPage.policyWording')}
                      fileUrl={productDocument?.policyWordingFile}
                    />
                  </Col>
                  <Col>
                    <DocumentItem
                      name={t('healthQuoteDetailsPage.planBrochure')}
                      fileUrl={productDocument?.brochureFile}
                    />
                  </Col>
                  <Col>
                    <DocumentItem
                      name={t('common:termsAndConditions')}
                      fileUrl={productDocument?.termsAndConditionsFile}
                    />
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export { BenefitSection };
